/**
 * Config Schema Types (used by handler/interaction registries)
 * and shared entry interfaces.
 */

import type { DataType } from './data-types';

export type ConfigFieldType =
  | 'text' | 'url' | 'email' | 'number' | 'boolean' | 'textarea' | 'select'
  | 'field_ref' | 'field_ref_list' | 'field_mapping_list' | 'template_text'
  | 'component_ref' | 'action_type_ref';

export interface ConfigField {
  key: string;
  type: ConfigFieldType;
  label: string;
  hint?: string;
  required?: boolean;
  defaultValue?: unknown;
  placeholder?: string;
  purpose?: string;
  userQuestion?: string;
  options?: Array<{ value: string; label: string; description?: string }>;
  itemSchema?: ConfigField[];
  registryFilter?: { compatibleWith?: string[] };
  visibleWhen?: {
    field: string;
    operator: 'equals' | 'not_equals' | 'exists' | 'not_exists';
    value?: string;
  };
  layout?: { width?: 'full' | 'half'; group?: string };
}

// Entry Interfaces

export type ActionScope = 'item' | 'bundle';

export type ActionCategory = 'decision' | 'communication' | 'integration' | 'navigation' | 'lifecycle' | 'bundle_synthesis';

export interface ActionTypeEntry {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly description: string;
  readonly category: ActionCategory;
  readonly defaultInteraction: string;
  readonly isDecision: boolean;
  readonly isTerminal: boolean;
  /**
   * Repeatable action (Deal Brief `regenerate`): executable any number of times on the
   * same item; never "completed" for button-hiding and excluded from the
   * all-non-terminal-completed conclude rule. Optional — absent means false, and only
   * `regenerate` sets it today.
   */
  readonly isRepeatable?: boolean;
  readonly executionLocation: 'frontend' | 'backend';
  readonly defaultOutcome?: { status: string };
  readonly requiredDataFields?: ReadonlyArray<{ key: string; type: DataType }>;
  readonly interactionPresets?: readonly InteractionPresetEntry[];
  readonly suggestedHandler?: string;
  readonly scope: ActionScope;
  readonly supportsBulk: boolean;
  readonly isRecommended: boolean;
}

export interface RequirementEntry {
  id: string;
  label: string;
  description?: string;
  fieldKeys: string[];
  optional?: boolean;
}

export interface HandlerEntry {
  id: string;
  label: string;
  description: string;
  /** Mirrored by the frontend wire enum in web/app-home/src/api/wiring.schema.ts (HandlerEntrySchema). */
  category: 'webhook' | 'crm_update' | 'notification' | 'internal' | 'none';
  configSchema: ConfigField[];
  wizardTabLabel?: string;
  summaryTemplate?: string;
  confirmVerb?: string;
  mergeInteractionTab?: boolean;
  requirements?: RequirementEntry[];
  suggestedInteraction?: string;
}

export interface InteractionEntry {
  id: string;
  label: string;
  description: string;
  configSchema: ConfigField[];
  readonly hasInputFields: boolean;
  /** Whether this interaction renders a UI surface (drawer/modal with form or review fields) */
  readonly hasSurface: boolean;
}

export type ArtifactCardPreview =
  | { type: 'template_card'; showActions: boolean; showFields: boolean }
  | { type: 'field_layout'; layout: 'vertical' | 'horizontal' | 'grid'; emphasis: 'title' | 'data' | 'metrics' }
  | { type: 'none'; reason: string };

// Shared (API-serializable) artifact card data — no React references

export interface SharedColumnDef {
  key: string;
  label?: string;
  width?: string;
  flex?: boolean;
  align?: 'left' | 'right';
  sortable?: boolean;
  filterable?: boolean;
}

export interface SharedTableColumnConfig {
  columns: SharedColumnDef[];
  rowActions: 'hover' | 'always' | 'none';
  searchableFields: string[];
}

// Preset Column Modifications

/**
 * A single column modification a preset can apply on top of the
 * card type's base column schema.
 *
 * - hide: remove the column from the rendered table
 * - width: override the column's width class
 * - order: explicit sort order (lower = earlier); unspecified columns
 *   keep their original relative order after all ordered columns
 */
export interface PresetColumnModification {
  key: string;
  hide?: boolean;
  width?: string;
  flex?: boolean;
  order?: number;
}

/**
 * Apply preset modifications to a base column list.
 *
 * Flow: card type base columns -> hide -> width/flex overrides -> reorder
 */
export function applyColumnModifications(
  baseColumns: readonly SharedColumnDef[],
  modifications: readonly PresetColumnModification[],
): SharedColumnDef[] {
  const modMap = new Map(modifications.map(m => [m.key, m]));

  // 1. Filter hidden columns
  let columns = baseColumns.filter(col => {
    const mod = modMap.get(col.key);
    return !mod?.hide;
  });

  // 2. Apply width/flex overrides
  columns = columns.map(col => {
    const mod = modMap.get(col.key);
    if (!mod) return col;
    const patched = { ...col };
    if (mod.width !== undefined) patched.width = mod.width;
    if (mod.flex !== undefined) patched.flex = mod.flex;
    return patched;
  });

  // 3. Reorder: columns with explicit order come first (sorted by order),
  //    then remaining columns in their original relative order
  const withOrder: Array<{ col: SharedColumnDef; order: number }> = [];
  const withoutOrder: SharedColumnDef[] = [];

  for (const col of columns) {
    const mod = modMap.get(col.key);
    if (mod?.order !== undefined) {
      withOrder.push({ col, order: mod.order });
    } else {
      withoutOrder.push(col);
    }
  }

  withOrder.sort((a, b) => a.order - b.order);
  return [...withOrder.map(w => w.col), ...withoutOrder];
}

export interface SharedArtifactCardBundleData {
  density: 'compact' | 'comfortable' | 'full';
  triageBehavior: 'fade-on-complete' | 'remove-on-complete' | null;
  progressIndicator: 'progress-bar' | 'count-badge' | 'none';
  accent?: string;  // CSS color token for bundle left border (e.g., 'blueberry-500')
}

export interface SharedArtifactCardCapabilities {
  supportsSelection: boolean;
  supportsKeyboardNav: boolean;
  supportsBulkActions: boolean;
}

export type SlotMode = 'card' | 'item-root';

export interface SharedSlotDefinition {
  readonly reserved: readonly string[];
  readonly mode: SlotMode;
}

export interface ArtifactCardEntry {
  id: string;
  label: string;
  description: string;
  supportsActions: boolean;
  /** Whether the card renders blueprint action labels as clickable inline buttons.
   *  False for content_card (uses "Read"), row types (click-to-action), batch_card (selection-based). */
  rendersActionButtons: boolean;
  preview: ArtifactCardPreview;
  capabilities: SharedArtifactCardCapabilities;
  slots: SharedSlotDefinition;
  accent?: string;  // CSS color token for bundle left border (e.g., 'blueberry-500')
  /** Sample data for wizard preview — named scenarios representing real-world use cases */
  sampleData?: {
    /** Named mock data sets; preview picks round-robin across rows */
    scenarios: Array<{ name: string; fields: Record<string, string> }>;
    /** Sample actions to show in preview */
    actions?: Array<{ id: string; label: string; isPrimary: boolean; interaction: string }>;
  };
  /**
   * Card-type-specific table columns. When present, TableLayout uses these
   * as the base column schema instead of the preset's tableColumns.
   * Preset columnModifications still apply on top.
   */
  tableColumns?: {
    columns: Array<{
      key: string;
      label: string;
      width?: string;
      flex?: boolean;
      sortable?: boolean;
      align?: 'right' | 'left';
    }>;
    sampleRows?: Array<Record<string, string>>;
    /** Table-specific actions (overrides sampleData.actions in table view) */
    sampleActions?: Array<{ id: string; label: string; isPrimary: boolean; interaction: string }>;
  };
  /**
   * Deprecated card types are kept in the registry so isValidRegistryId()
   * still accepts them during the transition window between migration and
   * code deploy. They should not appear in the wizard or be assigned to
   * new blueprints. `migratesTo` indicates the canonical replacement.
   */
  deprecated?: boolean;
  migratesTo?: string;
}

export interface LayoutEntry {
  id: string;
  label: string;
  icon: string;
}

export type ComponentPreview =
  | { type: 'mock_form'; fields: MockPreviewField[]; actions?: MockPreviewAction[] }
  | { type: 'mock_steps'; steps: Array<{ label: string; description?: string }> }
  | { type: 'none'; reason: string };

export interface MockPreviewField {
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  prefillFrom?: string;
  readonly?: boolean;
  options?: string[];
  toolbar?: boolean;
  results?: Array<{ label: string; sublabel: string; selected?: boolean }>;
}

export interface MockPreviewAction {
  label: string;
  variant: 'primary' | 'secondary';
}

export interface InteractionPresetEntry {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly interactionMode: string;
  readonly inputFields?: ReadonlyArray<{
    key: string;
    type: DataType;
    label: string;
    required?: boolean;
    placeholder?: string;
    prefillFrom?: string;
    displayMode?: 'input' | 'review';
  }>;
  readonly requiredDataFields?: ReadonlyArray<{ key: string; type: DataType; purpose: string }>;
}

/**
 * Check whether a string is a valid ID in an array-based registry.
 * Type predicate: on `true`, narrows `id` to the registry's literal ID union
 * (e.g. `ArtifactCard`) — provided the registry is `as const` or a tuple so
 * `T['id']` is a narrow literal type.
 */
export function isValidRegistryId<T extends { readonly id: string }>(
  registry: readonly T[],
  id: string,
): id is T['id'] {
  return registry.some((e) => e.id === id);
}

/**
 * Build a non-empty tuple of registry IDs for z.enum() consumption.
 *
 * z.enum requires `[T, ...T[]]` at compile time, but `registry.map(e => e.id)`
 * produces `T[]`. Instead of casting at each call site, prove non-emptiness
 * at runtime here — registries are always populated at module load, but
 * TypeScript can't see that from `readonly T[]` alone.
 */
export function registryIdsAsTuple<T extends { id: string }>(
  registry: readonly T[],
): readonly [T['id'], ...T['id'][]] {
  const [firstEntry, ...restEntries] = registry;
  if (firstEntry === undefined) {
    throw new Error('registryIdsAsTuple: registry is empty');
  }
  return [firstEntry.id, ...restEntries.map((e) => e.id)];
}
